import { FunctionComponent } from "react";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import {
  AttachmentCategory,
  CategoryLimits,
  describeLimits,
  formatSizeMB,
  getAttachmentCategory,
  validateAttachments,
} from "../../../utils/attachment_validator";

interface FileDropZoneProps {
  type: string;
  required?: boolean;
  onChange: (files: File[]) => void;
  multiSelect: boolean;
  className?: string;
  maxFileSizeMB?: number;
  limits?: CategoryLimits;
  existingFiles?: File[];
  maxTotalSizeMB?: number;
  /** Categories that upload outside the request body, e.g. videos to S3. */
  excludeFromTotal?: AttachmentCategory[];
  id?: string;
}

const FileDropZone1: FunctionComponent<FileDropZoneProps> = (props) => {
  const { showToast } = useToast();
  const inputId = props.id ?? "files";

  const MAX_FILE_SIZE_MB = props.maxFileSizeMB ?? 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const isValidFileType = (file: File, accept: string) => {
    const category = getAttachmentCategory(file);

    return accept.split(",").some((type) => {
      const trimmed = type.trim();

      // image/* or video/*
      if (trimmed.endsWith("/*")) {
        const prefix = trimmed.replace("/*", "/");
        if (file.type.startsWith(prefix)) return true;
        // Some pickers leave `type` empty — fall back to the extension.
        return !file.type && `${category}/` === prefix;
      }

      // exact match like application/pdf
      if (file.type === trimmed) return true;
      return !file.type && trimmed === "application/pdf" && category === "doc";
    });
  };

  const validateFiles = (files: File[]) => {
    for (const file of files) {
      if (!isValidFileType(file, props.type)) {
        showToast({
          type: ToastType.error,
          text: `"${file.name}" is not a supported file type. Allowed: ${props.type}`,
        });
        return false;
      }
    }

    if (props.limits) {
      const { error } = validateAttachments({
        incoming: files,
        existing: props.existingFiles,
        limits: props.limits,
        maxTotalSizeMB: props.maxTotalSizeMB,
        excludeFromTotal: props.excludeFromTotal,
      });

      if (error) {
        showToast({ type: ToastType.error, text: error });
        return false;
      }

      return true;
    }

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        showToast({
          type: ToastType.error,
          text: `"${file.name}" is ${formatSizeMB(
            file.size,
          )} — files must be under ${MAX_FILE_SIZE_MB} MB.`,
        });
        return false;
      }
    }

    return true;
  };

  const hint = props.limits
    ? describeLimits(props.limits)
    : `Supported formats: ${props.type} | Max size: ${MAX_FILE_SIZE_MB} MB`;

  return (
    <div className="h-full w-full">
      <label htmlFor={inputId} className={props.className ?? "h-full w-full"}>
        <div>
          <div
            id={`${inputId}-drop-zone`}
            className="flex flex-col justify-center items-center h-full w-full bg-gray-100 border border-gray-400 cursor-pointer hover:border-green-400 p-5 rounded"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const droppedFiles = e.dataTransfer.files;
              if (!droppedFiles || droppedFiles.length === 0) {
                showToast({
                  type: ToastType.error,
                  text: "Please select at least one file.",
                });
                return;
              }

              const files = [...droppedFiles];

              if (!validateFiles(files)) return;

              if (props.multiSelect) {
                props.onChange(files);
              } else {
                props.onChange([files[0]]);
              }

              e.dataTransfer.clearData();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <p className="text-gray-400">Drag and drop files</p>
          </div>
          <p className="text-xs text-gray-400 mt-2 font-semibold">{hint}</p>
        </div>
      </label>

      <input
        className="focus:outline-none"
        type="file"
        name="files"
        id={inputId}
        accept={props.type}
        multiple={props.multiSelect}
        required={props.required}
        hidden
        onChange={(e) => {
          const selectedFiles = e.target.files;
          if (!selectedFiles || selectedFiles.length === 0) return;

          const files = [...selectedFiles];
          if (!validateFiles(files)) {
            e.target.value = "";
            return;
          }

          props.onChange(files);
        }}
      />
    </div>
  );
};

export default FileDropZone1;
